import { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';

/** How long the shield lingers after the page is visible again. */
export const SHIELD_RELEASE_MS = 150;

function isPageHidden(): boolean {
  return (
    typeof document !== 'undefined' && document.visibilityState === 'hidden'
  );
}

/**
 * Touch devices only: there, losing window focus means the app went behind
 * the OS task switcher. On a desktop a blur is just a click on the address
 * bar, another window or a browser side panel, and covering the app then
 * reads as the page having gone blank.
 */
function coversOnBlur(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(pointer: coarse)').matches
  );
}

/**
 * P1-S2 — an opaque plum layer over the whole viewport while the page is in
 * the background (or, on touch devices, the window lost focus), so the OS
 * task switcher and screenshots of the app card show nothing. Solid on
 * purpose: a CSS blur would still leak shapes. It is removed ~150 ms after
 * the page is back, and a tap on it lifts it at once: someone tapping the
 * app is looking at it, whatever the focus events said.
 */
export default function PrivacyShield() {
  const [covered, setCovered] = useState<boolean>(isPageHidden);

  useEffect(() => {
    let releaseTimer: ReturnType<typeof setTimeout> | null = null;

    const cover = () => {
      if (releaseTimer !== null) {
        clearTimeout(releaseTimer);
        releaseTimer = null;
      }
      // Paint before the page is snapshotted, not on the next microtask.
      flushSync(() => setCovered(true));
    };
    const release = () => {
      if (releaseTimer !== null) clearTimeout(releaseTimer);
      releaseTimer = setTimeout(() => {
        releaseTimer = null;
        setCovered(false);
      }, SHIELD_RELEASE_MS);
    };

    const onVisibilityChange = () => {
      if (isPageHidden()) cover();
      else release();
    };
    // blur / focus do not bubble, so these only see the window itself.
    const onWindowBlur = () => {
      if (coversOnBlur()) cover();
    };
    const onWindowFocus = () => {
      if (!isPageHidden()) release();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('blur', onWindowBlur);
    window.addEventListener('focus', onWindowFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('blur', onWindowBlur);
      window.removeEventListener('focus', onWindowFocus);
      if (releaseTimer !== null) clearTimeout(releaseTimer);
    };
  }, []);

  if (!covered) return null;

  return (
    <div
      data-testid="privacy-shield"
      aria-hidden="true"
      className="fixed inset-0 z-[100] bg-plum-800"
      onPointerDown={() => setCovered(false)}
    />
  );
}
